import { getTimezone } from "@/lib/timezone";

import { AnsweredFormDetail } from "@/types/forms/answered-form";
import {
  MeasurementField,
  MeasurementGroupWithFields,
  MeasurementValueSummary,
} from "@/types/measurement";
import { PatientSummary } from "@/types/patient";
import {
  CheckInAppointmentResponse,
  PatientPortalAppointmentsResponse,
  PatientPortalFormsResponse,
} from "@/types/patient-portal";
import { WearableConnectSession, WearableConnection } from "@/types/wearable";

import { requestData } from "@/api/api-config";

export interface PatientPortalCustomMeasurementsResponse {
  groups: MeasurementGroupWithFields[];
  fields: MeasurementField[];
  values: MeasurementValueSummary[];
}

export async function getPatientCustomMeasurements(): Promise<PatientPortalCustomMeasurementsResponse | null> {
  return await requestData<PatientPortalCustomMeasurementsResponse>(
    "patient/patient-portal/custom-measurements",
    "get",
    // Calculated values pair their inputs by day, on the clock this page shows.
    { searchParams: { tz: getTimezone() } },
  );
}

export async function getPatientWearables(): Promise<
  WearableConnection[] | null
> {
  return await requestData<WearableConnection[]>(
    "patient/patient-portal/wearables",
    "get",
  );
}

export async function createPatientWearableConnectSession(
  connectionId?: string,
): Promise<WearableConnectSession | null> {
  return await requestData<WearableConnectSession>(
    "patient/patient-portal/wearables/connect-session",
    "post",
    { searchParams: { connection_id: connectionId } },
  );
}

/**
 * Tell the backend the patient just authorised this device, rather than waiting
 * for the aggregator's own report to be swept up tomorrow.
 *
 * `terraUserId` comes out of the redirect's query string, so it is a claim the
 * browser could have altered. The backend treats it as one: it asks the
 * aggregator whose id that is and refuses unless the answer names this patient.
 * Nothing here needs to be trusted, which is why it is safe to send at all.
 *
 * `catchError` is left on, so a refusal resolves to null rather than throwing —
 * the caller is a callback page whose job is to close, and the swept report is
 * still coming either way.
 */
export async function confirmPatientWearableConnection(
  terraUserId: string,
): Promise<WearableConnection[] | null> {
  return await requestData<WearableConnection[]>(
    "patient/patient-portal/wearables/confirm-connection",
    "post",
    { json: { terra_user_id: terraUserId } },
  );
}

/**
 * Rejects on failure rather than resolving to null — the portal twin of the
 * chart's disconnect, and it must not tell a patient their device is
 * disconnected when the aggregator refused.
 */
export async function disconnectPatientWearable(
  connectionId: string,
): Promise<WearableConnection | null> {
  return await requestData<WearableConnection>(
    `patient/patient-portal/wearables/${connectionId}`,
    "delete",
    undefined,
    false,
  );
}

export interface PatientPortalSignUpParams {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export async function getPatientMe(
  throwOnError = true,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    "patient/patient-portal/me",
    "get",
    undefined,
    !throwOnError,
  );
}

export async function patientPortalSignUp(
  params: PatientPortalSignUpParams = {},
): Promise<boolean> {
  const data = await requestData<{ success?: boolean }>(
    "patient/patient-portal/sign-up",
    "post",
    { json: params },
    false,
  );
  return data?.success === true;
}

export async function getPatientAppointments(): Promise<PatientPortalAppointmentsResponse | null> {
  return await requestData<PatientPortalAppointmentsResponse>(
    "patient/patient-portal/appointments",
    "get",
  );
}

export async function cancelPatientAppointment(
  appointmentId: string,
  reason?: string,
): Promise<boolean> {
  const data = await requestData<{ success?: boolean }>(
    `patient/patient-portal/appointments/${appointmentId}/cancel`,
    "post",
    { json: { reason: reason ?? null } },
    false,
  );
  return data?.success === true;
}

export async function checkInPatientAppointment(
  appointmentId: string,
): Promise<CheckInAppointmentResponse | null> {
  // Pass catchError=false so HTTP errors (403 not-attendee, 400 outside
  // window) reject the promise instead of resolving to null — otherwise the
  // mutation's onError never fires and the UI silently treats a failure as
  // success.
  return await requestData<CheckInAppointmentResponse>(
    `patient/patient-portal/appointments/${appointmentId}/check-in`,
    "post",
    undefined,
    false,
  );
}

export async function getPatientForms(): Promise<PatientPortalFormsResponse | null> {
  return await requestData<PatientPortalFormsResponse>(
    "patient/patient-portal/forms",
    "get",
  );
}

export async function getPatientPortalForm(
  formPath: string,
  answeredFormId?: string,
): Promise<AnsweredFormDetail | null> {
  return await requestData<AnsweredFormDetail>(
    `patient/patient-portal/forms/${formPath.replace(/\.$/, "")}`,
    "get",
    answeredFormId
      ? { searchParams: { answered_form_id: answeredFormId } }
      : undefined,
    false,
  );
}

export interface PatientPortalProfileUpdate {
  firstName?: string | null;
  lastName?: string | null;
  address?: string | null;
  addressLineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  pronouns?: string | null;
  preferredName?: string | null;
}

export interface ShopShippingAddressUpdate {
  address: string;
  addressLineTwo?: string | null;
  city: string;
  state: string;
  zipCode: string;
}

export async function updateShopShippingAddress(
  update: ShopShippingAddressUpdate,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    "patient/shop/shipping-address",
    "put",
    { json: update },
    false,
  );
}

export async function updatePatientMe(
  update: PatientPortalProfileUpdate,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    "patient/patient-portal/me",
    "put",
    {
      json: update,
    },
    false,
  );
}

// Only for a patient with no home location yet; the clinic changes it after that.
export async function setPatientPrimaryLocation(
  locationId: string,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    "patient/patient-portal/me/primary-location",
    "put",
    {
      json: { locationId },
    },
    false,
  );
}
